/**
 * Leitura tipada dos erros da API Acertei.
 *
 * O backend Go não tem struct de erro padronizada — são ~350 `gin.H` inline,
 * sem código de erro (a única exceção em todo o projeto é `EMAIL_NOT_VERIFIED`,
 * em `cmd/api/auth.go`). Então a classificação aqui é feita por
 * **(status HTTP, texto da mensagem)**, e este arquivo é o único lugar do
 * frontend que precisa saber disso.
 *
 * Três armadilhas do backend que este módulo existe para absorver:
 *
 * 1. **~20 handlers escrevem `"Error"` com E maiúsculo** (`auth.go:92`,
 *    `events.go`, `mocktests.go`, `questions.go:338`, `registereds.go`,
 *    `resolutions.go:256`…). Ler só `data.error` devolve `undefined` neles.
 * 2. **Assinatura vencida chega como 401**, não 402 nem 403
 *    (`middleware.go:161-217`). Tratar todo 401 como "faça login" expulsa da
 *    sessão justamente o assinante que só precisa renovar o plano.
 * 3. **Falta de assinatura também aparece como 404** nas leituras filtradas por
 *    plano (`questions.go:97,215,281`, `subjects.go:84`, `subscribers.go:87`).
 */

/** Motivo pelo qual o backend recusou a requisição. */
export type DenialKind =
  /** 401 sem token, token malformado ou usuário inexistente. */
  | 'unauthenticated'
  /** 401 com `exp` vencido, ou sessão derrubada por outro login. */
  | 'session-expired'
  /** 401 e 404 de assinatura — o usuário está logado, o plano é que não cobre. */
  | 'subscription'
  /** 403 de papel: a rota é de administrador. */
  | 'admin-only'
  /** 403 sobre recurso de outra pessoa. */
  | 'ownership'
  /** 403 sem causa reconhecida — a mensagem do backend é a explicação. */
  | 'forbidden'
  /** 403 no login, com `code: "EMAIL_NOT_VERIFIED"`. */
  | 'email-not-verified'
  /** 403 de chave de API sem escopo `write` (caminho do MCP, não do navegador). */
  | 'readonly-key'
  /** 429 do rate limiter. */
  | 'rate-limited';

export interface ApiError {
  status?: number;
  /** Único código existente hoje: `EMAIL_NOT_VERIFIED`. */
  code?: string;
  /** Mensagem crua da API (`data.error ?? data.Error`), sempre string. */
  raw: string;
  /** `undefined` = não é negativa de permissão; trate como erro comum. */
  kind?: DenialKind;
}

/**
 * Rotas públicas de credencial. Um 401 aqui significa "senha errada", não
 * "sessão inválida" — deslogar seria absurdo, e essas telas
 * (`Login.view`, `Registro.view`, `EsqueciSenha.view`…) já mostram o erro
 * no próprio formulário.
 */
const PUBLIC_AUTH_ROUTES = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-email',
  '/auth/resend-verification-email',
  '/auth/forgot-password',
  '/auth/reset-password-by-token',
  '/auth/check-availability',
];

export function isPublicAuthRoute(url?: string): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/\/+$/, '');
  return PUBLIC_AUTH_ROUTES.some((route) => path.endsWith(route));
}

/**
 * Sem acentos e em minúsculas, para as regras abaixo não dependerem de o
 * `Content-Type` do Gin ter chegado em UTF-8 intacto.
 */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** `data.error ?? data.Error`, tolerando objeto (`websocket/handler.go:30`). */
function readRawMessage(data: unknown): string {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return '';

  const bag = data as Record<string, unknown>;
  const value = bag.error ?? bag.Error;

  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const nested = (value as Record<string, unknown>).message;
    if (typeof nested === 'string') return nested;
  }
  return '';
}

/**
 * A ordem importa: dentro de cada status, a primeira regra que casar vence, e
 * o fallback do status vem por último. O caso da assinatura precisa ser testado
 * antes do fallback de 401 — é ele que impede o logout indevido.
 */
const RULES: Array<{
  status: number;
  code?: string;
  match?: RegExp;
  kind: DenialKind;
}> = [
  { status: 429, kind: 'rate-limited' },

  { status: 403, code: 'EMAIL_NOT_VERIFIED', kind: 'email-not-verified' },
  { status: 403, match: /somente leitura/, kind: 'readonly-key' },
  { status: 403, match: /voce nao pode/, kind: 'ownership' },
  // "Acesso restrito a administradores" (files.go:21, ~25 rotas),
  // "Apenas administradores podem criar flashcards" (review_cards.go:135),
  // "Only master or administrator can…" (questions.go:122, question_bulk.go:41).
  { status: 403, match: /administrador|administrator/, kind: 'admin-only' },
  // Resto do 403 — "Simulado ja encerrado.", "Acesso nao permitido"… Aqui a
  // mensagem do backend É a explicação, então não inventamos uma.
  { status: 403, kind: 'forbidden' },

  { status: 401, match: /assinatura|assinante|conta expirou|acesso deste usuario esta expirado/, kind: 'subscription' },
  { status: 401, match: /sessao expirada/, kind: 'session-expired' },
  { status: 401, kind: 'unauthenticated' },

  { status: 404, match: /assinatura nao encontrada|assinante nao encontrado/, kind: 'subscription' },
];

export function readApiError(err: unknown): ApiError {
  const response = (err as { response?: { status?: number; data?: unknown } } | undefined)?.response;
  const status = response?.status;
  const data = response?.data;

  const raw = readRawMessage(data);
  const code =
    data && typeof data === 'object' && typeof (data as Record<string, unknown>).code === 'string'
      ? ((data as Record<string, unknown>).code as string)
      : undefined;

  const folded = fold(raw);
  const kind = RULES.find(
    (rule) =>
      rule.status === status &&
      (rule.code === undefined || rule.code === code) &&
      (rule.match === undefined || rule.match.test(folded)),
  )?.kind;

  return { status, code, raw, kind };
}

/** `true` quando o erro é uma negativa de permissão, e não uma falha comum. */
export function isDenial(err: unknown): boolean {
  return readApiError(err).kind !== undefined;
}
